import React, { useState, useRef, useContext, useCallback } from 'react';
import {
    Dialog,
    DialogType,
    DialogFooter,
    PrimaryButton,
    DefaultButton,
    Stack,
    Text,
    MessageBar,
    MessageBarType,
    ChoiceGroup,
    IChoiceGroupOption,
    Icon,
} from '@fluentui/react';
import { ThemeContext } from '../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../Common/util/localStorageUtils';
import { FlagOverride } from '../models/EnvironmentModels';
import { parseFlagOverridesCsv, FlagOverrideCsvImportResult, exportFlagOverridesToCsv, downloadCsv } from '../utils/CsvUtils';

interface CsvImportDialogProps {
    hidden: boolean;
    onDismiss: () => void;
    onImport: (overrides: FlagOverride[], mode: 'append' | 'replace') => void;
    existingCount: number;
}

export const CsvImportDialog: React.FC<CsvImportDialogProps> = ({
    hidden,
    onDismiss,
    onImport,
    existingCount,
}) => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [importResult, setImportResult] = useState<FlagOverrideCsvImportResult | null>(null);
    const [fileName, setFileName] = useState<string>('');
    const [importMode, setImportMode] = useState<'append' | 'replace'>('append');
    const [isDragging, setIsDragging] = useState(false);
    const [fileReadError, setFileReadError] = useState<string | null>(null);

    const resetState = useCallback(() => {
        setImportResult(null);
        setFileName('');
        setImportMode('append');
        setIsDragging(false);
        setFileReadError(null);
    }, []);

    const handleDismiss = useCallback(() => {
        resetState();
        onDismiss();
    }, [resetState, onDismiss]);

    const processFile = useCallback((file: File) => {
        setFileReadError(null);

        if (!file.name.toLowerCase().endsWith('.csv')) {
            setFileReadError('Please select a CSV file (.csv)');
            return;
        }

        setFileName(file.name);

        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target?.result as string;
            if (!text || text.trim() === '') {
                setFileReadError('The selected file is empty');
                return;
            }
            const result = parseFlagOverridesCsv(text);
            setImportResult(result);
        };
        reader.onerror = () => {
            setFileReadError('Failed to read the file. Please try again.');
        };
        reader.readAsText(file);
    }, []);

    const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            processFile(file);
        }
        // Reset the input so re-selecting the same file triggers the event
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [processFile]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const file = e.dataTransfer.files?.[0];
        if (file) {
            processFile(file);
        }
    }, [processFile]);

    const handleConfirmImport = useCallback(() => {
        if (importResult && importResult.overrides.length > 0) {
            onImport(importResult.overrides, importMode);
            resetState();
        }
    }, [importResult, importMode, onImport, resetState]);

    const handleDownloadTemplate = useCallback(() => {
        const templateCsv = exportFlagOverridesToCsv([]);
        downloadCsv(templateCsv, 'FeatureFlagOverrides_Template.csv');
    }, []);

    const modeOptions: IChoiceGroupOption[] = [
        { key: 'append', text: 'Append', iconProps: { iconName: 'Add' } },
        { key: 'replace', text: 'Replace all existing', iconProps: { iconName: 'Refresh' } },
    ];

    const hasErrors = importResult !== null && importResult.errors.length > 0;
    const hasValidData = importResult !== null && importResult.overrides.length > 0;
    const showOnlyErrors = hasErrors && !hasValidData;

    return (
        <Dialog
            hidden={hidden}
            onDismiss={handleDismiss}
            dialogContentProps={{
                type: DialogType.normal,
                title: 'Import Feature Flag Overrides from CSV',
            }}
            modalProps={{
                isBlocking: false,
            }}
            minWidth={650}
        >
            <Stack tokens={{ childrenGap: 16 }}>
                {/* Drop zone */}
                <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                        border: `2px dashed ${isDragging ? theme.palette.themePrimary : theme.palette.neutralTertiaryAlt}`,
                        borderRadius: '8px',
                        padding: '32px 16px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        backgroundColor: isDragging ? theme.palette.themeLighterAlt : theme.palette.neutralLighterAlt,
                        transition: 'all 0.2s ease',
                    }}
                >
                    <Icon
                        iconName={fileName ? 'CheckMark' : 'CloudUpload'}
                        styles={{
                            root: {
                                fontSize: 36,
                                color: fileName ? theme.palette.green : theme.palette.themePrimary,
                                marginBottom: '8px',
                                display: 'block',
                            },
                        }}
                    />
                    {fileName ? (
                        <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>
                            {fileName}
                        </Text>
                    ) : (
                        <>
                            <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>
                                Drag & drop a CSV file here
                            </Text>
                            <Text variant="small" styles={{ root: { color: theme.palette.neutralSecondary, display: 'block', marginTop: '4px' } }}>
                                or click to browse
                            </Text>
                        </>
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                    />
                </div>

                {/* Download template link */}
                <DefaultButton
                    text="Download CSV Template"
                    iconProps={{ iconName: 'DownloadDocument' }}
                    onClick={handleDownloadTemplate}
                    styles={{ root: { alignSelf: 'flex-start' } }}
                />

                {/* File read error */}
                {fileReadError && (
                    <MessageBar
                        messageBarType={MessageBarType.error}
                        styles={{
                            root: { backgroundColor: theme.palette.redDark },
                            text: { color: theme.palette.white },
                            icon: { color: theme.palette.white },
                        }}
                    >
                        {fileReadError}
                    </MessageBar>
                )}

                {/* Import results preview */}
                {importResult && (
                    <div style={{ padding: '12px', backgroundColor: theme.palette.neutralLighterAlt, borderRadius: '4px' }}>
                        <Text variant="mediumPlus" styles={{ root: { fontWeight: 600, marginBottom: '8px', display: 'block' } }}>
                            Preview
                        </Text>
                        <Stack tokens={{ childrenGap: 6 }}>
                            <Text variant="small">
                                <strong>Total rows:</strong> {importResult.totalRows}
                            </Text>
                            <Text variant="small" styles={{ root: { color: importResult.validRows > 0 ? theme.palette.green : theme.palette.neutralSecondary } }}>
                                <strong>Valid rows:</strong> {importResult.validRows}
                            </Text>
                            {importResult.skippedRows > 0 && (
                                <Text variant="small" styles={{ root: { color: theme.palette.orange } }}>
                                    <strong>Skipped rows:</strong> {importResult.skippedRows}
                                </Text>
                            )}
                            {existingCount > 0 && (
                                <Text variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
                                    <strong>Existing overrides:</strong> {existingCount}
                                </Text>
                            )}
                        </Stack>

                        {/* Show first few override names as preview */}
                        {importResult.overrides.length > 0 && (
                            <div style={{ marginTop: '8px' }}>
                                <Text variant="small" styles={{ root: { fontWeight: 600, display: 'block', marginBottom: '4px' } }}>
                                    Overrides to import:
                                </Text>
                                <div style={{
                                    maxHeight: '120px',
                                    overflowY: 'auto',
                                    padding: '8px',
                                    backgroundColor: theme.palette.white,
                                    borderRadius: '4px',
                                    border: `1px solid ${theme.palette.neutralLight}`,
                                }}>
                                    {importResult.overrides.map((o, i) => (
                                        <Text key={i} variant="small" styles={{ root: { display: 'block', padding: '2px 0' } }}>
                                            {i + 1}. <strong>{o.name}</strong>
                                            {o.enabled ? ' (Enabled)' : ' (Disabled)'}
                                            {o.serviceTreeId ? ` — ST: ${o.serviceTreeId.substring(0, 8)}...` : ''}
                                            {o.accountName ? ` — ${o.accountName}/${o.projectName || ''}` : ''}
                                        </Text>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Validation errors */}
                {hasErrors && (
                    <MessageBar
                        messageBarType={showOnlyErrors ? MessageBarType.error : MessageBarType.warning}
                        isMultiline
                        styles={{
                            root: {
                                backgroundColor: showOnlyErrors ? theme.palette.redDark : '#4d3a00',
                                maxHeight: '150px',
                                overflowY: 'auto',
                            },
                            text: { color: '#ffffff' },
                            icon: { color: showOnlyErrors ? '#ffffff' : '#ffb900' },
                            innerText: { color: '#ffffff' },
                        }}
                    >
                        <strong>{importResult!.errors.length} validation error{importResult!.errors.length !== 1 ? 's' : ''}:</strong>
                        <ul style={{ margin: '4px 0 0 0', paddingLeft: '16px' }}>
                            {importResult!.errors.slice(0, 10).map((err, i) => (
                                <li key={i} style={{ fontSize: '12px' }}>
                                    Row {err.row}{err.column ? `, ${err.column}` : ''}: {err.message}
                                </li>
                            ))}
                            {importResult!.errors.length > 10 && (
                                <li style={{ fontSize: '12px', fontStyle: 'italic' }}>
                                    ...and {importResult!.errors.length - 10} more
                                </li>
                            )}
                        </ul>
                    </MessageBar>
                )}

                {/* Import mode selection */}
                {hasValidData && (
                    <div style={{ padding: '12px', backgroundColor: theme.palette.white, borderRadius: '4px', border: `1px solid ${theme.palette.neutralLight}` }}>
                        <ChoiceGroup
                            label="Import mode"
                            selectedKey={importMode}
                            options={modeOptions}
                            onChange={(_, option) => {
                                if (option) setImportMode(option.key as 'append' | 'replace');
                            }}
                            styles={{ flexContainer: { display: 'flex', gap: '20px' } }}
                        />
                        {importMode === 'replace' && existingCount > 0 && (
                            <MessageBar
                                messageBarType={MessageBarType.warning}
                                styles={{
                                    root: { marginTop: '8px', backgroundColor: '#4d3a00' },
                                    text: { color: '#ffffff' },
                                    icon: { color: '#ffb900' },
                                    innerText: { color: '#ffffff' },
                                }}
                            >
                                This will replace all {existingCount} existing override{existingCount !== 1 ? 's' : ''} with {importResult!.validRows} imported override{importResult!.validRows !== 1 ? 's' : ''}. This action can be undone by reloading without saving.
                            </MessageBar>
                        )}
                    </div>
                )}
            </Stack>

            <DialogFooter>
                <PrimaryButton
                    onClick={handleConfirmImport}
                    text={`Import ${importResult?.validRows ?? 0} Override${(importResult?.validRows ?? 0) !== 1 ? 's' : ''}`}
                    disabled={!hasValidData}
                />
                <DefaultButton onClick={handleDismiss} text="Cancel" />
            </DialogFooter>
        </Dialog>
    );
};
